import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShieldAlert, Activity, Network, IdCard, Folder, Loader2, ChevronDown, Cpu } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { JsonToggle } from '@/components/common/JsonToggle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { drillApi, type DrillResponse, type DrillBucket } from '@/api/endpoints';
import { formatDate, bsonId } from '@/lib/utils';
import { Truncated } from '@/components/common/Truncated';
import { toast } from '@/store/toast';

type EntityType = 'hwid' | 'user_id' | 'nickname' | 'file_md5' | 'ip_address';

const PER_PAGE = 200;

function num(v: number | undefined | null): string {
  if (v == null) return '0';
  return v.toLocaleString('pt-BR');
}

function pick(d: any, ...keys: string[]): any {
  for (const k of keys) if (d && d[k] != null) return d[k];
  return null;
}

// ====== Kpi card =============================================
function Kpi({ label, value, hint, tone }: { label: string; value: string | number; hint?: string; tone?: 'default' | 'danger' | 'warn' | 'ok' }) {
  const toneClass = tone === 'danger' ? 'text-destructive' : tone === 'warn' ? 'text-warning' : tone === 'ok' ? 'text-success' : 'text-foreground';
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-bold ${toneClass} tabular-nums`}>{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

// ====== Tabela paginada (Carregar mais) =========================
function BucketTable({
  title,
  bucket,
  columns,
  loadingMore,
  onLoadMore
}: {
  title: string;
  bucket: DrillBucket | undefined;
  columns: { key: string; label: string; render?: (row: any) => React.ReactNode; className?: string }[];
  loadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  if (!bucket || bucket.total === 0) return <EmptyState title={`Nenhum ${title.toLowerCase()}`} />;
  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <CardTitle className="text-sm font-medium">
          {title} <Badge variant="outline" className="ml-1">{num(bucket.total)} no total</Badge>
          <span className="text-xs text-muted-foreground ml-2">Exibindo {bucket.items.length} de {num(bucket.total)}</span>
        </CardTitle>
        <JsonToggle data={bucket.items} label="Ver JSON" />
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-auto max-h-[60vh]">
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                {columns.map(c => <TableHead key={c.key} className={c.className}>{c.label}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {bucket.items.map((row, i) => (
                <TableRow key={bsonId(row._id, `${title}-${i}`)}>
                  {columns.map(c => (
                    <TableCell key={c.key} className={c.className}>
                      {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {bucket.hasMore && onLoadMore && (
          <div className="p-3 flex justify-center border-t">
            <Button variant="outline" size="sm" onClick={onLoadMore} disabled={loadingMore}>
              {loadingMore ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ChevronDown className="h-3 w-3 mr-1" />}
              Carregar mais (+{Math.min(PER_PAGE, bucket.total - bucket.items.length)})
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ====== Página principal =============================================
export function DrillPage() {
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState<EntityType>((params.get('type') as EntityType) ?? 'hwid');
  const [value, setValue] = useState(params.get('value') ?? '');
  const [historic, setHistoric] = useState(params.get('historic') === 'true');
  const [data, setData] = useState<DrillResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState<string | null>(null);

  const fetchDrill = useCallback(async (skip = 0) => {
    if (!value) return;
    setLoading(true);
    try {
      let res: any;
      const opts = { historic, perCollection: PER_PAGE, skip };
      if (type === 'hwid') res = await drillApi.hwid(value, opts);
      else if (type === 'user_id') res = await drillApi.user(Number(value), opts);
      else if (type === 'nickname') res = await drillApi.nick(value, { historic, perCollection: PER_PAGE });
      else if (type === 'file_md5') res = await drillApi.file(value, opts);
      else if (type === 'ip_address') res = await drillApi.ip(value, opts);
      setData(res);
      const np = new URLSearchParams(params);
      np.set('type', type); np.set('value', value); np.set('historic', String(historic));
      setParams(np);
    } catch (e: any) {
      toast.error('Falha ao consultar', e.message ?? 'Erro inesperado.');
    } finally {
      setLoading(false);
    }
  }, [type, value, historic, params, setParams]);

  useEffect(() => {
    if (value) fetchDrill(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== load-more por categoria =====
  const loadMore = useCallback(async (category: 'cheat' | 'dll' | 'files' | 'auth' | 'disconnects' | 'nicks') => {
    if (!data) return;
    const skip = (() => {
      if (category === 'cheat') return data.detections.cheat.items.length;
      if (category === 'dll') return data.detections.dll.items.length;
      if (category === 'files') return data.detections.files.items.length;
      if (category === 'auth') return data.sessions.auth.items.length;
      if (category === 'disconnects') return data.sessions.disconnects.items.length;
      return data.identity.nicks.items.length;
    })();
    setLoadingMore(category);
    try {
      const opts = { historic, perCollection: PER_PAGE, skip };
      let res: any;
      if (type === 'hwid') res = await drillApi.hwid(value, opts);
      else if (type === 'user_id') res = await drillApi.user(Number(value), opts);
      else if (type === 'file_md5') res = await drillApi.file(value, opts);
      else if (type === 'ip_address') res = await drillApi.ip(value, opts);
      else return;
      const r = res as DrillResponse;
      setData(prev => prev ? {
        ...prev,
        detections: {
          cheat: category === 'cheat' ? { ...r.detections.cheat, items: [...prev.detections.cheat.items, ...r.detections.cheat.items] } : prev.detections.cheat,
          dll: category === 'dll' ? { ...r.detections.dll, items: [...prev.detections.dll.items, ...r.detections.dll.items] } : prev.detections.dll,
          files: category === 'files' ? { ...r.detections.files, items: [...prev.detections.files.items, ...r.detections.files.items] } : prev.detections.files
        },
        sessions: {
          auth: category === 'auth' ? { ...r.sessions.auth, items: [...prev.sessions.auth.items, ...r.sessions.auth.items] } : prev.sessions.auth,
          disconnects: category === 'disconnects' ? { ...r.sessions.disconnects, items: [...prev.sessions.disconnects.items, ...r.sessions.disconnects.items] } : prev.sessions.disconnects
        },
        identity: {
          nicks: category === 'nicks' ? { ...r.identity.nicks, items: [...prev.identity.nicks.items, ...r.identity.nicks.items] } : prev.identity.nicks
        }
      } : null);
    } catch (e: any) {
      toast.error('Falha ao carregar mais', e.message ?? 'Erro inesperado.');
    } finally {
      setLoadingMore(null);
    }
  }, [data, historic, type, value]);

  const totals = data?.totals;
  const grand = useMemo(() => totals?.grandTotalDetections ?? 0, [totals]);

  return (
    <div>
      <PageHeader title="Detalhamento" subtitle="Dossiê consolidado por HWID, usuário, nickname, arquivo ou IP — base recente (7 dias) ou histórica (30 dias)." />

      {/* ===== Toolbar ===== */}
      <Card className="mb-4">
        <CardContent className="pt-4">
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={type} onValueChange={v => setType(v as EntityType)}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hwid">HWID</SelectItem>
                <SelectItem value="user_id">User ID</SelectItem>
                <SelectItem value="nickname">Nickname</SelectItem>
                <SelectItem value="file_md5">File MD5</SelectItem>
                <SelectItem value="ip_address">IP</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Valor da entidade…"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="max-w-xl font-mono text-xs"
              onKeyDown={e => e.key === 'Enter' && fetchDrill(0)}
            />
            <Button onClick={() => fetchDrill(0)} disabled={!value || loading}>
              {loading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Buscar
            </Button>
            <label className="flex items-center gap-2 text-sm text-muted-foreground ml-2">
              <input type="checkbox" checked={historic} onChange={e => setHistoric(e.target.checked)} />
              Incluir histórico (30 dias)
            </label>
            {type === 'hwid' && value && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/hardware?hwid=${encodeURIComponent(value)}`}>
                  <Cpu className="h-3 w-3 mr-1" /> Ver hardware
                </Link>
              </Button>
            )}
            {data && <JsonToggle data={data} label="Ver JSON completo" />}
          </div>
        </CardContent>
      </Card>

      {loading && !data && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>}

      {!loading && !data && <EmptyState title="Informe um valor para começar" description="Selecione o tipo de entidade e digite o valor a investigar" />}

      {data && (
        <>
          {/* ===== KPI cards ===== */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            <Kpi label="Detecções" value={num(grand)} tone={grand > 100 ? 'danger' : grand > 10 ? 'warn' : 'default'} hint="cheat + DLL + arquivos" />
            <Kpi label="Cheat" value={num(totals?.detectionCheat)} tone={totals && totals.detectionCheat > 0 ? 'danger' : 'default'} />
            <Kpi label="DLL" value={num(totals?.detectionDll)} tone={totals && totals.detectionDll > 0 ? 'warn' : 'default'} />
            <Kpi label="Arquivos" value={num(totals?.detectionFiles)} tone={totals && totals.detectionFiles > 50 ? 'warn' : 'default'} />
            <Kpi label="Desconexões" value={num(totals?.sessionDisconnect)} />
            <Kpi label="Autenticações" value={num(totals?.sessionAuth)} />
          </div>

          {/* ===== Header info ===== */}
          <Card className="mb-4">
            <CardContent className="pt-5 grid md:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground uppercase">Entidade</div>
                <div className="text-xs"><Truncated mono value={data.entity.value} maxChars={50} /></div>
                <Badge variant="outline" className="mt-1">{data.entity.type}</Badge>
                <Badge variant={data.historic ? 'warning' : 'success'} className="ml-1">{data.historic ? 'Histórico (30 dias)' : 'Recente (7 dias)'}</Badge>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Marcação atual</div>
                {data.mark
                  ? <div className="flex flex-wrap gap-1 mt-1"><Badge>{data.mark.status}</Badge><Badge variant="outline">severidade {data.mark.severity ?? 0}</Badge>{(data.mark.tags ?? []).slice(0,4).map((t:string)=><Badge key={t} variant="secondary">{t}</Badge>)}</div>
                  : <div className="text-muted-foreground italic mt-1">— sem marcação</div>}
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Casos relacionados</div>
                <div className="text-2xl font-bold tabular-nums">{data.relatedCases?.length ?? 0}</div>
              </div>
            </CardContent>
          </Card>

          {/* ===== Tabs ===== */}
          <Tabs defaultValue="detections">
            <TabsList>
              <TabsTrigger value="detections"><ShieldAlert className="h-3 w-3 mr-1" /> Detecções</TabsTrigger>
              <TabsTrigger value="sessions"><Activity className="h-3 w-3 mr-1" /> Sessões</TabsTrigger>
              <TabsTrigger value="identity"><IdCard className="h-3 w-3 mr-1" /> Identidade</TabsTrigger>
              <TabsTrigger value="cases"><Folder className="h-3 w-3 mr-1" /> Casos ({data.relatedCases?.length ?? 0})</TabsTrigger>
            </TabsList>

            <TabsContent value="detections" className="mt-3">
              <BucketTable
                title="Detecções de Cheat"
                bucket={data.detections.cheat}
                loadingMore={loadingMore === 'cheat'}
                onLoadMore={() => loadMore('cheat')}
                columns={[
                  { key: 'ts', label: 'Data e hora', className: 'text-xs whitespace-nowrap', render: r => formatDate(pick(r, 'ts', 'timestampUtc')) },
                  { key: 'detectionType', label: 'Tipo', className: 'text-xs whitespace-nowrap' },
                  { key: 'riskLevel', label: 'Risco', render: r => <Badge variant="destructive">{r.riskLevel ?? r.riskScore ?? '—'}</Badge> },
                  { key: 'modulePath', label: 'Caminho / Detalhe', className: 'text-xs', render: r => <Truncated mono value={pick(r, 'modulePath', 'detail', 'message', 'fileMd5', 'sourceHash')} maxChars={80} /> },
                  { key: 'clientId', label: 'Cliente', className: 'text-xs' },
                  { key: 'userId', label: 'Usuário', className: 'text-xs' }
                ]}
              />
              <BucketTable
                title="Detecções de DLL"
                bucket={data.detections.dll}
                loadingMore={loadingMore === 'dll'}
                onLoadMore={() => loadMore('dll')}
                columns={[
                  { key: 'ts', label: 'Data e hora', className: 'text-xs whitespace-nowrap', render: r => formatDate(pick(r, 'ts')) },
                  { key: 'modulePath', label: 'Caminho do módulo', className: 'text-xs', render: r => <Truncated mono value={r.modulePath} maxChars={70} /> },
                  { key: 'sourceSigner', label: 'Assinatura', className: 'text-xs', render: r => <Badge variant={r.sourceSigner === 'trusted' ? 'success' : r.sourceSigner === 'unsigned' ? 'destructive' : 'outline'}>{r.sourceSigner ?? '—'}</Badge> },
                  { key: 'riskLevel', label: 'Risco', render: r => <Badge variant="warning">{r.riskLevel ?? '—'}</Badge> },
                  { key: 'detectionType', label: 'Tipo', className: 'text-xs whitespace-nowrap' },
                  { key: 'clientId', label: 'Cliente', className: 'text-xs' }
                ]}
              />
              <BucketTable
                title="Detecções de Arquivos"
                bucket={data.detections.files}
                loadingMore={loadingMore === 'files'}
                onLoadMore={() => loadMore('files')}
                columns={[
                  { key: 'ts', label: 'Data e hora', className: 'text-xs whitespace-nowrap', render: r => formatDate(pick(r, 'ts')) },
                  { key: 'modulePath', label: 'Arquivo', className: 'text-xs', render: r => <Truncated mono value={pick(r, 'modulePath', 'detail')} maxChars={70} /> },
                  { key: 'fileExtension', label: 'Extensão', className: 'text-xs', render: r => <Badge variant="outline">{r.fileExtension ?? '—'}</Badge> },
                  { key: 'fileMd5', label: 'MD5', className: 'text-xs', render: r => <Truncated mono value={r.fileMd5} maxChars={16} /> },
                  { key: 'sourceSigner', label: 'Assinatura', className: 'text-xs', render: r => <Badge variant={r.sourceSigner === 'trusted' ? 'success' : r.sourceSigner === 'unsigned' ? 'destructive' : 'outline'}>{r.sourceSigner ?? '—'}</Badge> },
                  { key: 'riskLevel', label: 'Risco', render: r => <Badge variant="warning">{r.riskLevel ?? '—'}</Badge> }
                ]}
              />
            </TabsContent>

            <TabsContent value="sessions" className="mt-3">
              <BucketTable
                title="Eventos de autenticação"
                bucket={data.sessions.auth}
                loadingMore={loadingMore === 'auth'}
                onLoadMore={() => loadMore('auth')}
                columns={[
                  { key: 'ts', label: 'Data e hora', className: 'text-xs whitespace-nowrap', render: r => formatDate(pick(r, 'ts')) },
                  { key: 'category', label: 'Categoria', className: 'text-xs', render: r => <Badge variant={r.category === 'auth_spoof' ? 'destructive' : 'outline'}>{r.category ?? '—'}</Badge> },
                  { key: 'reason', label: 'Razão', className: 'text-xs', render: r => <Truncated value={r.reason} maxChars={40} /> },
                  { key: 'ipAddress', label: 'IP', className: 'text-xs', render: r => <Truncated mono value={r.ipAddress} maxChars={20} /> },
                  { key: 'userId', label: 'Usuário', className: 'text-xs' },
                  { key: 'detail', label: 'Detalhe', className: 'text-xs', render: r => <Truncated value={pick(r, 'detail', 'message')} maxChars={50} /> }
                ]}
              />
              <BucketTable
                title="Desconexões"
                bucket={data.sessions.disconnects}
                loadingMore={loadingMore === 'disconnects'}
                onLoadMore={() => loadMore('disconnects')}
                columns={[
                  { key: 'ts', label: 'Data e hora', className: 'text-xs whitespace-nowrap', render: r => formatDate(pick(r, 'ts')) },
                  { key: 'event', label: 'Evento', className: 'text-xs whitespace-nowrap', render: r => r.event ?? '—' },
                  { key: 'reason', label: 'Razão', className: 'text-xs', render: r => <Truncated value={r.reason} maxChars={30} /> },
                  { key: 'ipAddress', label: 'IP', className: 'text-xs', render: r => <Truncated mono value={r.ipAddress} maxChars={20} /> },
                  { key: 'userId', label: 'Usuário', className: 'text-xs' },
                  { key: 'nickname', label: 'Nick', className: 'text-xs', render: r => <Truncated value={pick(r, 'nickname', 'characterName')} maxChars={20} /> }
                ]}
              />
            </TabsContent>

            <TabsContent value="identity" className="mt-3">
              <BucketTable
                title="Nicks e logins observados"
                bucket={data.identity.nicks}
                loadingMore={loadingMore === 'nicks'}
                onLoadMore={() => loadMore('nicks')}
                columns={[
                  { key: 'ts', label: 'Data e hora', className: 'text-xs whitespace-nowrap', render: r => formatDate(pick(r, 'ts')) },
                  { key: 'nickname', label: 'Nickname', className: 'text-sm font-medium', render: r => <Truncated value={r.nickname} maxChars={30} /> },
                  { key: 'characterName', label: 'Character', className: 'text-xs', render: r => <Truncated value={r.characterName} maxChars={30} /> },
                  { key: 'loginId', label: 'Login', className: 'text-xs', render: r => <Truncated value={r.loginId} maxChars={30} /> },
                  { key: 'userId', label: 'Usuário', className: 'text-xs' },
                  { key: 'ipAddress', label: 'IP', className: 'text-xs', render: r => <Truncated mono value={r.ipAddress} maxChars={20} /> }
                ]}
              />
            </TabsContent>

            <TabsContent value="cases" className="mt-3">
              {(!data.relatedCases || data.relatedCases.length === 0)
                ? <EmptyState title="Nenhum caso relacionado" />
                : <Card><CardContent className="pt-4">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>#</TableHead><TableHead>Status</TableHead><TableHead>Título</TableHead><TableHead>Criado</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {data.relatedCases.map((c: any, i: number) => (
                          <TableRow key={c.caseNumber ?? bsonId(c._id, i)}>
                            <TableCell className="font-mono text-xs">{c.caseNumber ?? '—'}</TableCell>
                            <TableCell><Badge>{c.status ?? '—'}</Badge></TableCell>
                            <TableCell>{c.title ?? '—'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(c.createdAtUtc)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent></Card>}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
